// =============================================================================
// Hosted-agent eval CASES — draft state, wire payload and client-side checks
//
// Split out of `hosted-agent-evals.ts` (the presentation half) so each file
// stays readable: this one is only about the EXAM — what an admin types into
// the case editor, how it becomes a request body, and what the server would
// reject before the round trip.
//
// The single rule that shapes the payload: the server model is
// `extra="forbid"`, so an expectation must carry ONLY the fields its `kind`
// uses. A stale `tool` left behind when the admin switches kind is a 400 for
// the whole case, not a harmless extra.
// =============================================================================

import type {
  HostedAgentEvalCase,
  HostedAgentEvalCaseInput,
  HostedAgentEvalExpectation,
  HostedAgentEvalExpectationKind,
  HostedAgentEvalToolDenialCode,
} from "@/api/admin-api"

import {
  DEFAULT_EVAL_CASE_TIMEOUT_SECONDS,
  HOSTED_AGENT_EVAL_TOOL_DENIAL_CODES,
  MAX_EVAL_CASE_NAME_LENGTH,
  MAX_EVAL_CASE_PROMPT_LENGTH,
  MAX_EVAL_CASE_TIMEOUT_SECONDS,
  MAX_EVAL_EXPECTATION_VALUE_LENGTH,
  MAX_EVAL_EXPECTATIONS_PER_CASE,
  MIN_EVAL_CASE_TIMEOUT_SECONDS,
} from "@/api/admin-api"

import { hostedAgentEvalExpectationFields } from "./hosted-agent-evals"

const MAX_EVAL_EXPECTATION_TOOL_LENGTH = 100
const MAX_EVAL_EXPECTATION_DETAIL_LENGTH = 200

// ---------------------------------------------------------------------------
// Case drafts (editor state)
// ---------------------------------------------------------------------------

export type HostedAgentEvalExpectationDraft = {
  /** Local-only React list key; never sent to the server. */
  key: string
  kind: HostedAgentEvalExpectationKind
  expected: string
  value: string
  caseSensitive: boolean
  tool: string
  action: string
  detailContains: string
  code: string
  minCount: string
  limit: string
}

export type HostedAgentEvalCaseDraft = {
  /** Server id, or null while the case has never been saved. */
  id: string | null
  name: string
  prompt: string
  enabled: boolean
  timeoutSeconds: string
  expectations: HostedAgentEvalExpectationDraft[]
}

let expectationKeySeed = 0

function nextExpectationKey(): string {
  expectationKeySeed += 1
  return `eval-expectation-${expectationKeySeed}`
}

export function createBlankHostedAgentEvalExpectation(): HostedAgentEvalExpectationDraft {
  return {
    key: nextExpectationKey(),
    kind: "run_status",
    expected: "completed",
    value: "",
    caseSensitive: false,
    tool: "",
    action: "",
    detailContains: "",
    code: "",
    minCount: "1",
    limit: "",
  }
}

export function createBlankHostedAgentEvalCase(): HostedAgentEvalCaseDraft {
  return {
    id: null,
    name: "",
    prompt: "",
    enabled: true,
    timeoutSeconds: String(DEFAULT_EVAL_CASE_TIMEOUT_SECONDS),
    expectations: [createBlankHostedAgentEvalExpectation()],
  }
}

export function toHostedAgentEvalCaseDraft(source: HostedAgentEvalCase): HostedAgentEvalCaseDraft {
  return {
    id: source.id,
    name: source.name,
    prompt: source.prompt,
    enabled: source.enabled !== false,
    timeoutSeconds: String(source.timeout_seconds),
    expectations: source.expectations.map((expectation) => ({
      key: nextExpectationKey(),
      kind: expectation.kind,
      expected: expectation.expected ?? "completed",
      value: expectation.value ?? "",
      caseSensitive: expectation.case_sensitive === true,
      tool: expectation.tool ?? "",
      action: expectation.action ?? "",
      detailContains: expectation.detail_contains ?? "",
      code: expectation.code ?? "",
      minCount: String(expectation.min_count ?? 1),
      limit: expectation.limit != null ? String(expectation.limit) : "",
    })),
  }
}

/**
 * Draft → wire payload, emitting only the fields the kind uses. Anything the
 * kind does not use is omitted entirely rather than sent as null, because the
 * server model forbids extras and would reject the whole case.
 */
export function toHostedAgentEvalCaseInput(
  draft: HostedAgentEvalCaseDraft
): HostedAgentEvalCaseInput {
  const timeout = Number(draft.timeoutSeconds.trim())
  return {
    name: draft.name.trim(),
    prompt: draft.prompt.trim(),
    enabled: draft.enabled,
    timeout_seconds: Number.isFinite(timeout) ? timeout : DEFAULT_EVAL_CASE_TIMEOUT_SECONDS,
    expectations: draft.expectations.map((expectation) => {
      const fields = hostedAgentEvalExpectationFields(expectation.kind)
      const payload: HostedAgentEvalExpectation = { kind: expectation.kind }
      if (fields.expected) {
        payload.expected = expectation.expected.trim() === "failed" ? "failed" : "completed"
      }
      if (fields.value) {
        payload.value = expectation.value.trim()
        payload.case_sensitive = expectation.caseSensitive
      }
      if (fields.tool) payload.tool = expectation.tool.trim()
      if (fields.action && expectation.action.trim()) payload.action = expectation.action.trim()
      if (fields.detailContains && expectation.detailContains.trim()) {
        payload.detail_contains = expectation.detailContains.trim()
      }
      if (fields.code && expectation.code.trim()) {
        payload.code = expectation.code.trim() as HostedAgentEvalToolDenialCode
      }
      if (fields.minCount) payload.min_count = Number(expectation.minCount.trim()) || 1
      if (fields.limit) payload.limit = Number(expectation.limit.trim())
      return payload
    }),
  }
}

// ---------------------------------------------------------------------------
// Client-side validation, mirroring `_check_kind_fields`
// ---------------------------------------------------------------------------

export type HostedAgentEvalCaseErrorCode =
  | "name_required"
  | "name_too_long"
  | "prompt_required"
  | "prompt_too_long"
  | "timeout_invalid"
  | "expectations_required"
  | "expectations_too_many"
  | "expectation_invalid"

export type HostedAgentEvalExpectationErrorCode =
  | "expected_required"
  | "value_required"
  | "value_too_long"
  | "tool_required"
  | "tool_too_long"
  | "detail_too_long"
  | "code_invalid"
  | "min_count_invalid"
  | "limit_required"

export type HostedAgentEvalCaseValidation = {
  case: HostedAgentEvalCaseErrorCode[]
  /** Keyed by the draft expectation's local `key`. */
  expectations: Record<string, HostedAgentEvalExpectationErrorCode>
}

export function validateHostedAgentEvalExpectation(
  draft: HostedAgentEvalExpectationDraft
): HostedAgentEvalExpectationErrorCode | null {
  const fields = hostedAgentEvalExpectationFields(draft.kind)
  if (fields.expected && !draft.expected.trim()) return "expected_required"
  if (fields.value) {
    if (!draft.value.trim()) return "value_required"
    if (draft.value.length > MAX_EVAL_EXPECTATION_VALUE_LENGTH) return "value_too_long"
  }
  if (fields.tool) {
    if (!draft.tool.trim()) return "tool_required"
    if (draft.tool.length > MAX_EVAL_EXPECTATION_TOOL_LENGTH) return "tool_too_long"
  }
  if (fields.detailContains && draft.detailContains.length > MAX_EVAL_EXPECTATION_DETAIL_LENGTH) {
    return "detail_too_long"
  }
  if (fields.code && draft.code.trim()) {
    const allowed: readonly string[] = HOSTED_AGENT_EVAL_TOOL_DENIAL_CODES
    if (!allowed.includes(draft.code.trim())) return "code_invalid"
  }
  if (fields.minCount) {
    const parsed = Number(draft.minCount.trim())
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 100) return "min_count_invalid"
  }
  if (fields.limit) {
    const parsed = Number(draft.limit.trim())
    if (!draft.limit.trim() || !Number.isFinite(parsed) || parsed < 0) return "limit_required"
  }
  return null
}

export function validateHostedAgentEvalCase(
  draft: HostedAgentEvalCaseDraft
): HostedAgentEvalCaseValidation {
  const caseErrors: HostedAgentEvalCaseErrorCode[] = []
  const name = draft.name.trim()
  if (!name) caseErrors.push("name_required")
  else if (name.length > MAX_EVAL_CASE_NAME_LENGTH) caseErrors.push("name_too_long")

  const prompt = draft.prompt.trim()
  if (!prompt) caseErrors.push("prompt_required")
  else if (prompt.length > MAX_EVAL_CASE_PROMPT_LENGTH) caseErrors.push("prompt_too_long")

  const timeout = Number(draft.timeoutSeconds.trim())
  if (
    !draft.timeoutSeconds.trim() ||
    !Number.isFinite(timeout) ||
    timeout < MIN_EVAL_CASE_TIMEOUT_SECONDS ||
    timeout > MAX_EVAL_CASE_TIMEOUT_SECONDS
  ) {
    caseErrors.push("timeout_invalid")
  }

  if (draft.expectations.length === 0) caseErrors.push("expectations_required")
  if (draft.expectations.length > MAX_EVAL_EXPECTATIONS_PER_CASE) {
    caseErrors.push("expectations_too_many")
  }

  const expectationErrors: Record<string, HostedAgentEvalExpectationErrorCode> = {}
  for (const expectation of draft.expectations) {
    const error = validateHostedAgentEvalExpectation(expectation)
    if (error) expectationErrors[expectation.key] = error
  }
  if (Object.keys(expectationErrors).length > 0) caseErrors.push("expectation_invalid")

  return { case: caseErrors, expectations: expectationErrors }
}

export function isHostedAgentEvalCaseValid(draft: HostedAgentEvalCaseDraft): boolean {
  return validateHostedAgentEvalCase(draft).case.length === 0
}
